<script>
import DayByDayEducator from '@/components/booking/bookingList/day-by-day-educator.vue'
import NamePlaceholder from '@/components/booking/bookingList/name-placeholder.vue'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import dayjs from '@/services/date'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import BookingType from '@/shared/constants/booking/BookingType'
import ClientBookingStatus from '@/shared/constants/booking/ClientBookingStatus'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import BookingStatusLabel from '@components/booking/booking-status-label.vue'
import { mapGetters } from 'vuex'
import CustomTooltip from '../custom-tooltip.vue'

export default {
  name: 'BookingDaysTable',
  components: {
    BookingStatusLabel,
    DayByDayEducator,
    NamePlaceholder,
    CustomTooltip,
  },
  mixins: [featureFlagMixin],
  props: {
    bookingId: { type: Number, required: true },
    blocks: { type: Array, required: true, default: () => [] },
    display: { type: Function, required: true },
  },
  data() {
    return {
      BookingStatusLabelType,
      ClientBookingStatus,
      BookingType,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
    }),
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    daysComputed() {
      if (!this.blocks.length) return []
      return this.blocks.map((bookingBlock) => {
        const candidate = bookingBlock.candidate
        const first = (candidate?.firstName || '').trim()
        const last = (candidate?.lastName || '').trim()
        const hasAny = !!(first || last)
        const name = hasAny
          ? `${first}${first && last ? ' ' : ''}${last}`
          : this.display(null)
        return {
          id: bookingBlock.id,
          date: this._formatDayForTable(bookingBlock.start),
          time: this._formatTimeRangeForTable(
            bookingBlock.start,
            bookingBlock.end
          ),
          name,
          status: bookingBlock.status,
          bookingStatus: bookingBlock.bookingStatus,
          candidate,
          isToday: bookingBlock.isToday,
          canEdit: bookingBlock.canEdit,
        }
      })
    },
  },
  methods: {
    // Time range
    _formatTimeRangeForTable(start, end) {
      if (!start || !end) return '-'
      const sh = start.getHours()
      const sMin = String(start.getMinutes()).padStart(2, '0')
      const startStr = `${sh}:${sMin}`

      const eh = String(end.getHours()).padStart(2, '0')
      const eMin = String(end.getMinutes()).padStart(2, '0')
      const endStr = `${eh}:${eMin}`

      return `${startStr} – ${endStr}`
    },
    // Days
    _formatDayForTable(d) {
      if (!d) return '-'
      const wd = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(
        d
      )
      const day = new Intl.DateTimeFormat(undefined, { day: 'numeric' }).format(
        d
      )
      const mon = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(d)
      const yr = new Intl.DateTimeFormat(undefined, { year: 'numeric' }).format(
        d
      )
      return `${wd}, ${day} ${mon} ${yr}`
    },
    editBookingDay(id, date) {
      if (!id) return false

      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: id },
        query: {
          mode: BookingType.SingleDay,
          date: dayjs(date).format(DateFormatToken.YYYYMMDD),
        },
      })
    },
  },
}
</script>

<template>
  <v-tab-item>
    <v-simple-table class="days-table">
      <thead>
        <tr>
          <th class="th">{{ $t('sidePanel.details') }}</th>
          <th class="th">{{ $t('sidePanel.time') }}</th>
          <th class="th">{{ $t('sidePanel.educator') }}</th>
          <th class="th text-center">{{ $t('sidePanel.status') }}</th>
          <th v-if="selfServiceFeatureFlags.enableEditBooking" class="th"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(day, index) in daysComputed" :key="`days-tr-${index}`">
          <td class="td">{{ day.date }}</td>
          <td class="td">{{ day.time }}</td>
          <td class="td td-educator">
            <template
              v-if="
                day.bookingStatus === BookingStatusLabelType.Open ||
                  !day.candidate
              "
            >
              <NamePlaceholder
                :text="
                  day.status === ClientBookingStatus.multiTentative
                    ? $t('booking.multipleEducators')
                    : $t('general.noEducator')
                "
              />
            </template>

            <template v-else>
              <DayByDayEducator :candidate="day.candidate" />
            </template>
          </td>
          <td class="td">
            <BookingStatusLabel :status="day.bookingStatus" />
          </td>
          <td v-if="selfServiceFeatureFlags.enableEditBooking" class="td">
            <CustomTooltip
              v-if="day.isToday"
              position="left"
              :text="$t('booking.editTodayBookingWarning')"
            >
              <BaseButton
                icon="mdi-pencil-outline"
                fab
                tertiary
                circle
                sm
                hover-color="grey lighten-4"
                class="panel-edit-disabled"
              />
            </CustomTooltip>
            <CustomTooltip
              v-if="day.canEdit"
              position="left"
              :text="$t('booking.editBookingDay')"
            >
              <BaseButton
                icon="mdi-pencil-outline"
                fab
                tertiary
                circle
                sm
                hover-color="grey lighten-4"
                class="panel-edit"
                :data-button="`booking-edit-day-${bookingId}`"
                @click="editBookingDay(day.id, day.date)"
              />
            </CustomTooltip>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
  </v-tab-item>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/side-panel.scss';
</style>
